import { Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import colors from "../../../../themes/colors";
import { allCategoriesRaw, vatCategories } from "../../../../utils";
import { ITransaction, TransactionSplit } from "../../../../types";

export enum TransactionSubmissionState {
  SUBMITTED = "SUBMITTED",
  NOT_SUBMITTED = "NOT_SUBMITTED",
  FIBU_CHANGED = "FIBU_CHANGED",
  VCC_CHANGED = "VCC_CHANGED",
  MULTIPLE_CHANGES = "MULTIPLE_CHANGES",
  UNKNOWN = "UNKNOWN",
  SPLIT = "SPLIT",
}

export const getTagColor = (status: TransactionSubmissionState) => {
  switch (status) {
    case TransactionSubmissionState.UNKNOWN:
      return colors.darkRed;
    case TransactionSubmissionState.NOT_SUBMITTED:
    case TransactionSubmissionState.SPLIT:
      return "default";
    case TransactionSubmissionState.SUBMITTED:
      return "success";
    default:
      return "orange";
  }
};

export const statusToTitle = (status: TransactionSubmissionState) => {
  switch (status) {
    case TransactionSubmissionState.UNKNOWN:
      return "Unknown";
    case TransactionSubmissionState.NOT_SUBMITTED:
      return "Not submitted";
    case TransactionSubmissionState.SUBMITTED:
      return "Submitted";
    case TransactionSubmissionState.FIBU_CHANGED:
      return "Fibu changed";
    case TransactionSubmissionState.VCC_CHANGED:
      return "VCC changed";
    case TransactionSubmissionState.MULTIPLE_CHANGES:
      return "Fibu & VCC changed";
    case TransactionSubmissionState.SPLIT:
      return "Split";
    default:
      return "";
  }
};

export const createGetTooltipTitle =
  (
    translateCategoryCode: (code?: string | null) => string,
    translateVatCategoryCode: (code?: string | null) => string
  ) =>
  (status: TransactionSubmissionState, record: ITransaction) => {
    let tooltipTitle = "";

    if (status === TransactionSubmissionState.MULTIPLE_CHANGES) {
      tooltipTitle = `Fibu: ${translateCategoryCode(
        record.submittedCategoryCode
      )} -> ${translateCategoryCode(
        record.categoryCode
      )} | VCC: ${translateVatCategoryCode(
        record.submittedVatCategoryCode
      )} -> ${translateVatCategoryCode(record.vatCategoryCode)}`;
    } else if (status === TransactionSubmissionState.FIBU_CHANGED) {
      tooltipTitle = `Fibu: ${translateCategoryCode(
        record.submittedCategoryCode
      )} -> ${translateCategoryCode(record.categoryCode)}`;
    } else if (status === TransactionSubmissionState.VCC_CHANGED) {
      tooltipTitle = `VCC: ${translateVatCategoryCode(
        record.submittedVatCategoryCode
      )} -> ${translateVatCategoryCode(record.vatCategoryCode)}`;
    } else if (status === TransactionSubmissionState.SPLIT) {
      tooltipTitle = "Split transaction. Please expand + for more details";
    }

    return tooltipTitle;
  };

const TransactionSubmissionStatus = ({
  record,
}: {
  record: ITransaction | Partial<TransactionSplit>;
}) => {
  const { t } = useTranslation();
  const translateCategoryCode = (code?: string | null) =>
    t(`categories.${code}`, {
      defaultValue:
        allCategoriesRaw.find(([value]) => value === code)?.[1] || code || "",
    });
  const translateVatCategoryCode = (code?: string | null) =>
    t(`categories.${code}`, {
      defaultValue:
        vatCategories.find(([value]) => value === code)?.[1] || code || "",
    });
  const getTooltipTitle = createGetTooltipTitle(
    translateCategoryCode,
    translateVatCategoryCode
  );
  const status =
    (record.submissionStatus as TransactionSubmissionState | null) ??
    TransactionSubmissionState.NOT_SUBMITTED;
  const color = getTagColor(status);
  const title = statusToTitle(status);
  const tooltipTitle = getTooltipTitle(status, record as ITransaction);
  const tag = <Tag color={color}>{title}</Tag>;

  if (tooltipTitle) {
    return (
      <Tooltip title={tooltipTitle} placement="topLeft">
        {tag}
      </Tooltip>
    );
  }

  return tag;
};

export default TransactionSubmissionStatus;
